import classnames from 'classnames';
import { useContext, useRef } from 'react';
import type React from 'react';
import SearchSource from '~/constants/srp/SearchSource';
import { useTrackEvent } from '~/context/srp/SearchAnalyticsContext';
import SearchEventsDataContext from '~/context/srp/SearchEventsDataContext';
import styles from './ClickableTile.module.scss';
import ObservableTile from '../ObservableTile/ObservableTile';

type OwnProps = React.PropsWithChildren<{
  analyticsId: string;
  backgroundLink?: string;
  bgOffsetY?: string;
  darkenBackground?: boolean;
  experiment?: boolean;
  handleBackground?: string;
  handleTileClick?: (e: React.MouseEvent) => void;
  isRelative?: boolean;
  link?: string;
  name?: string;
  nativeAction?: string;
  nativeIncluded?: boolean;
  style?: { [key: string]: string };
  tileId?: string;
}>;

export default function ClickableTile({
  analyticsId,
  backgroundLink,
  children,
  experiment = false,
  handleTileClick = () => null,
  isRelative = false,
  link,
  name,
  nativeAction,
  nativeIncluded,
  style,
  tileId
}: OwnProps) {
  const { setSearchSource } = useContext(SearchEventsDataContext);
  const trackEvent = useTrackEvent();

  const trackClick = () => {
    trackEvent('Conditional tile - clicked', {
      tile: name
    });
  };

  const handleClick: React.MouseEventHandler<HTMLAnchorElement> = e => {
    trackClick();
    handleTileClick(e);
    if (tileId === 'GreatDealTile') {
      setSearchSource(SearchSource.Tile);
    }
  };

  const ref = useRef(null);

  return (
    <ObservableTile
      observeRef={ref}
      name={name}
      onVisible={() => {
        trackEvent('Conditional tile - viewed', { tile: name });
      }}
    >
      <div
        className={classnames(
          'm:rounded-[6px] m:border m:border-solid m:border-grey-2',
          styles.ctWrapper,
          tileId,
          {
            [styles.ctWrapperExperiment]: experiment,
            relative: isRelative
          }
        )}
        id={name}
      >
        <a
          className={classnames(styles.ctAnchor, {
            [styles.ctAnchorNoPadding]: backgroundLink,
            relative: isRelative
          })}
          data-native-payload={
            nativeAction && JSON.stringify({ action: nativeAction })
          }
          data-analytics-id={analyticsId}
          data-native-included={nativeIncluded}
          style={{ ...style }}
          href={link}
          id={tileId}
          onClick={handleClick}
          onContextMenu={trackClick}
        >
          {children}
        </a>
      </div>
    </ObservableTile>
  );
}
